import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  ChartPanel,
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
  DashboardsPageFrame,
  DataTable,
  KpiCard,
  KpiGrid,
  type DataTableColumn,
} from "./dashboardKit";
import { CarespaceCountLink } from "./carespaceCountLink";
import { CarespaceRecordsProvider } from "./carespaceRecords";
import { HsaMonthlyChart, HsaProviderQuadrant, HsaRateTrend } from "./respiteCharts";
import type {
  RespiteHsaBuckets,
  RespiteHsaCountKey,
  RespiteReferralCounts,
} from "./respiteMetrics";
import { respiteNeedsAttentionQueryOptions, respiteSummaryQueryOptions } from "./respiteReactQuery";
import { downloadRespiteNeedsAttentionCsv } from "./respiteCsv";
import {
  readRespiteState,
  respiteWindow,
  shiftMonth,
  writeRespiteState,
  type RespiteViewState,
} from "./respiteState";
import type { RespiteNeedsAttentionRow, RespiteSummaryResponse } from "./respiteTypes";
import { respiteView, type RespiteView } from "./respiteView";

const DASHBOARD_TIMEZONE = "America/New_York";
const MONTH_OPTION_COUNT = 24;

function todayIsoInTimezone(timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function asCount(value: number): string {
  return Math.round(value).toLocaleString();
}

function asPct(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function share(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

function formatTimestamp(value: string | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DASHBOARD_TIMEZONE,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

function humanize(value: string): string {
  return value.replaceAll("_", " ");
}

function FilterSelect(props: {
  id: string;
  label: string;
  allLabel: string;
  onChange: (value: string) => void;
  options: string[];
  value: string;
}) {
  const options =
    props.value && !props.options.includes(props.value)
      ? [props.value, ...props.options]
      : props.options;
  return (
    <DashboardFilterField htmlFor={props.id} label={props.label}>
      <DashboardSelect
        id={props.id}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">{props.allLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </DashboardSelect>
    </DashboardFilterField>
  );
}

/** A provider group row, with its sites as child rows when it has more than one. */
type ProviderRow<T> = T & { name: string; sites?: Array<ProviderRow<T>> };

function providerRows<T>(
  groups: Array<T & { providerGroup: string; providers: Array<T & { providerName: string }> }>,
): Array<ProviderRow<T>> {
  return groups.map(({ providerGroup, providers, ...counts }) =>
    // A group with one site in view is just that site, under its own name; nothing to expand.
    providers.length === 1
      ? { ...(counts as T), name: providers[0]!.providerName }
      : {
          ...(counts as T),
          name: providerGroup,
          sites: providers.map(({ providerName, ...site }) => ({
            ...(site as T),
            name: providerName,
          })),
        },
  );
}

function countColumn<T extends { ids: Record<K, string[]> } & Record<K, number>, K extends string>(
  key: K,
  label: string,
  options: { definition?: string; emphasize?: boolean } = {},
): DataTableColumn<ProviderRow<T>> {
  return {
    key,
    label,
    align: "right",
    sortable: true,
    ...(options.definition ? { definition: options.definition } : {}),
    render: (row) => (
      <span className={options.emphasize && row[key] ? "font-semibold text-red-700" : undefined}>
        <CarespaceCountLink
          count={row[key]}
          recordIds={row.ids[key]}
          label={`${row.name}: ${label}`}
        />
      </span>
    ),
    sortValue: (row) => row[key],
  };
}

const PROVIDER_NAME_COLUMN = {
  key: "name",
  label: "Provider",
  sortable: true,
  definition:
    "Sites that share a Pylon account are grouped under one provider; expand a provider to see its sites.",
};

const HSA_COLUMNS: Array<DataTableColumn<ProviderRow<RespiteHsaBuckets>>> = [
  {
    ...PROVIDER_NAME_COLUMN,
    definition: `${PROVIDER_NAME_COLUMN.definition} An HSA is credited to the provider on the latest home-visit referral, otherwise on the latest in-home respite referral (non-cancelled first). Day centers do not do HSAs.`,
  },
  countColumn<RespiteHsaBuckets, RespiteHsaCountKey>("cohort", "Carespaces"),
  countColumn<RespiteHsaBuckets, RespiteHsaCountKey>("completedOnTime", "≤60 days"),
  countColumn<RespiteHsaBuckets, RespiteHsaCountKey>("completedLate", ">60 days"),
  countColumn<RespiteHsaBuckets, RespiteHsaCountKey>("openOverdue", "Overdue", { emphasize: true }),
  countColumn<RespiteHsaBuckets, RespiteHsaCountKey>("openInWindow", "Within 60 days"),
  {
    key: "goalRate",
    label: "≤60-day rate",
    align: "right",
    sortable: true,
    definition: "Completed within 60 days ÷ (completed + requested and past 60 days).",
    render: (row) => asPct(row.goalRate),
    sortValue: (row) => row.goalRate,
  },
];

type ReferralKey = keyof RespiteReferralCounts["ids"];

const REFERRAL_COLUMNS: Array<DataTableColumn<ProviderRow<RespiteReferralCounts>>> = [
  PROVIDER_NAME_COLUMN,
  countColumn<RespiteReferralCounts, ReferralKey>("pending", "Pending"),
  countColumn<RespiteReferralCounts, ReferralKey>("inProgress", "In progress"),
  countColumn<RespiteReferralCounts, ReferralKey>("completed", "Completed"),
  countColumn<RespiteReferralCounts, ReferralKey>("cancelled", "Cancelled", {
    definition:
      "Cancelled with no later referral to another respite provider, and the carespace is still aligned: a lost referral.",
  }),
  countColumn<RespiteReferralCounts, ReferralKey>("rereferred", "Re-routed", {
    definition:
      "Cancelled, then referred to another respite provider (for example after a ZIP-coverage re-route).",
  }),
  countColumn<RespiteReferralCounts, ReferralKey>("leftProgram", "Left program", {
    definition:
      "Cancelled, not re-routed, and the carespace is now archived or not aligned (for example after death, hospice or a move). Uses the carespace's status today.",
  }),
  countColumn<RespiteReferralCounts, ReferralKey>("archived", "Archived"),
  {
    key: "openCarePlanUpdated",
    label: "Updated since referral",
    align: "right",
    sortable: true,
    definition:
      "Share of open referrals with a goal, intervention or care-plan note added or updated since the referral.",
    render: (row) => asPct(share(row.openCarePlanUpdated, row.open)),
    sortValue: (row) => share(row.openCarePlanUpdated, row.open),
  },
];

type CountyRow = RespiteView["counties"][number];

const COUNTY_COLUMNS: Array<DataTableColumn<CountyRow>> = [
  { key: "state", label: "State", sortable: true },
  { key: "county", label: "County", sortable: true },
  {
    key: "count",
    label: "Respite-eligible, no provider",
    align: "right",
    sortable: true,
    render: (row) => (
      <CarespaceCountLink
        count={row.count}
        recordIds={row.ids}
        label={`${row.county}, ${row.state}: respite-eligible, no provider`}
      />
    ),
  },
];

const NEEDS_ATTENTION_COLUMNS: Array<DataTableColumn<RespiteNeedsAttentionRow>> = [
  {
    key: "patientName",
    label: "Patient",
    sortable: true,
    render: (row) => (
      <a
        href={row.carespaceUrl}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-blue-700 hover:underline"
      >
        {row.patientName}
      </a>
    ),
  },
  { key: "clinicName", label: "Clinic", sortable: true },
  { key: "navigatorName", label: "CN", sortable: true },
  { key: "providerName", label: "Provider", sortable: true },
  {
    key: "guideState",
    label: "Guide state",
    sortable: true,
    render: (row) => humanize(row.guideState),
  },
  {
    key: "respiteEligible",
    label: "Respite eligible",
    sortable: true,
    render: (row) => (row.respiteEligible ? "Yes" : "No"),
    sortValue: (row) => (row.respiteEligible ? 1 : 0),
  },
  {
    key: "engagementStatus",
    label: "Status",
    sortable: true,
    render: (row) => humanize(row.engagementStatus),
  },
  { key: "daysOpen", label: "Days open", align: "right", sortable: true },
  {
    key: "pylonStatus",
    label: "Pylon",
    sortable: true,
    render: (row) => (row.pylonStatus ? humanize(row.pylonStatus) : "—"),
  },
  {
    key: "carePlanOnFile",
    label: "Care plan",
    sortable: true,
    render: (row) =>
      row.carePlanUpdatedSinceReferral
        ? "Updated since referral"
        : row.carePlanOnFile
          ? "On file"
          : "None",
    sortValue: (row) => Number(row.carePlanOnFile) + Number(row.carePlanUpdatedSinceReferral),
  },
];

function NeedsAttentionPanel(props: { filters: RespiteViewState; today: string }) {
  const query = useQuery(
    respiteNeedsAttentionQueryOptions({
      clinic: props.filters.clinic,
      navigator: props.filters.navigator,
      provider: props.filters.provider,
    }),
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const rows = query.data?.rows ?? [];

  const download = async () => {
    setExporting(true);
    setExportError("");
    try {
      await downloadRespiteNeedsAttentionCsv(rows, props.today);
    } catch {
      setExportError("The list could not be downloaded. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ChartPanel
      title="Needs attention: open referrals for archived, unaligned or ineligible carespaces"
      action={
        <button
          type="button"
          disabled={!rows.length || exporting}
          onClick={() => void download()}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {exporting ? "Preparing CSV…" : "Download CSV"}
        </button>
      }
    >
      <p className="mb-3 text-xs text-muted-foreground">
        Pending or in-progress respite and home-visit referrals, regardless of the PAF window.
        {query.data?.source === "mock"
          ? " Showing sample data: the live query is unavailable."
          : ""}
      </p>
      {exportError ? (
        <p role="alert" className="mb-3 text-xs text-destructive">
          {exportError}
        </p>
      ) : null}
      {query.isPending ? (
        <p className="py-6 text-sm text-muted-foreground">Loading referrals…</p>
      ) : query.isError ? (
        <p className="py-6 text-sm text-destructive">The needs-attention list is unavailable.</p>
      ) : (
        <DataTable
          columns={NEEDS_ATTENTION_COLUMNS}
          rows={rows}
          rowKey={(row) => row.engagementId}
          defaultSort={{ key: "daysOpen", direction: "desc" }}
          maxHeightClass="max-h-[560px]"
          emptyMessage="No open referrals need attention."
        />
      )}
    </ChartPanel>
  );
}

function RespiteContent(props: { summary: RespiteSummaryResponse }) {
  const view = useMemo(() => respiteView(props.summary), [props.summary]);
  const { hsa, referrals } = view;
  const missing = props.summary.missingInitialPaf.map((ref) => ref.carespaceId);

  return (
    <CarespaceRecordsProvider filePrefix="respite" records={view.records}>
      <HsaRateTrend summary={props.summary} view={view} />

      <KpiGrid className="md:grid-cols-3">
        <KpiCard
          label="HSAs requested by caregiver"
          value={
            <CarespaceCountLink
              count={hsa.requested}
              recordIds={hsa.ids.requested}
              label="HSAs requested by caregiver"
            />
          }
          tooltip="Caregiver answered Yes to wanting a home safety assessment (D01)."
          footnote={`of ${asCount(hsa.cohort)} aligned, respite-eligible carespaces`}
        />
        <KpiCard
          label="HSAs overdue"
          value={
            <CarespaceCountLink
              count={hsa.openOverdue}
              recordIds={hsa.ids.openOverdue}
              label="HSAs overdue"
            />
          }
          tooltip="Requested, not completed, and more than 60 days since the initial PAF."
          footnote={
            <>
              <CarespaceCountLink
                count={hsa.openInWindow}
                recordIds={hsa.ids.openInWindow}
                label="HSAs still within 60 days"
              />{" "}
              more still within 60 days
            </>
          }
        />
        <KpiCard
          label="HSAs completed"
          value={
            <CarespaceCountLink
              count={hsa.completed}
              recordIds={hsa.ids.completed}
              label="HSAs completed"
            />
          }
          footnote={
            <>
              <CarespaceCountLink
                count={hsa.completedLate}
                recordIds={hsa.ids.completedLate}
                label="HSAs completed after 60 days"
              />{" "}
              after 60 days
            </>
          }
        />
        <KpiCard
          label="Open respite referrals"
          value={
            <CarespaceCountLink
              count={referrals.open}
              recordIds={referrals.ids.open}
              label="Open respite referrals"
            />
          }
          tooltip="Pending or in-progress referrals to contracted respite providers (in home, day center, 24h facility), referred in the window."
        />
        <KpiCard
          label="Care plan updated since referral"
          value={asPct(share(referrals.openCarePlanUpdated, referrals.open))}
          tooltip="Open respite referrals with a goal, intervention or care-plan note added or updated since the referral (the care-plan work Care Navigation credits)."
          footnote={
            <>
              <CarespaceCountLink
                count={referrals.openCarePlanUpdated}
                recordIds={referrals.ids.openCarePlanUpdated}
                label="Open referrals with care plan updated"
              />{" "}
              of {asCount(referrals.open)} open referrals
            </>
          }
        />
        <KpiCard
          label="Missing initial PAF"
          value={
            <CarespaceCountLink
              count={missing.length}
              recordIds={missing}
              label="Missing initial PAF"
            />
          }
          tooltip="Aligned, respite-eligible carespaces with no submitted initial PAF, so no 60-day clock. Not limited to the window."
        />
      </KpiGrid>

      <HsaProviderQuadrant view={view} />

      <HsaMonthlyChart view={view} />

      <ChartPanel title="HSA completion by provider">
        <DataTable
          columns={HSA_COLUMNS}
          rows={providerRows(view.hsaGroups)}
          childRows={(row) => row.sites}
          rowKey={(row) => row.name}
          defaultSort={{ key: "goalRate", direction: "asc" }}
          emptyMessage="No HSA cohort in this window."
        />
      </ChartPanel>

      <ChartPanel title="Respite referrals by provider (latest referral per carespace, referred in the window)">
        <DataTable
          columns={REFERRAL_COLUMNS}
          rows={providerRows(view.referralGroups)}
          childRows={(row) => row.sites}
          rowKey={(row) => row.name}
          defaultSort={{ key: "inProgress", direction: "desc" }}
          emptyMessage="No respite referrals in this window."
        />
      </ChartPanel>

      <ChartPanel title="Respite-eligible without a provider, by county">
        <p className="mb-3 text-xs text-muted-foreground">
          Aligned carespaces whose latest FAST score is 5+, with an active caregiver and no pending
          or in-progress respite referral, by the patient&apos;s primary address. Current as of
          today; follows the clinic and CN filters, not the months.
        </p>
        <DataTable
          columns={COUNTY_COLUMNS}
          rows={view.counties}
          rowKey={(row) => `${row.state}/${row.county}`}
          defaultSort={{ key: "count", direction: "desc" }}
          maxHeightClass="max-h-[420px]"
          emptyMessage="No respite-eligible carespaces without a provider."
        />
      </ChartPanel>
    </CarespaceRecordsProvider>
  );
}

export function RespiteDashboard() {
  const today = todayIsoInTimezone(DASHBOARD_TIMEZONE);
  const currentMonth = today.slice(0, 7);
  const [state, setState] = useState<RespiteViewState>(() =>
    readRespiteState(typeof window === "undefined" ? "" : window.location.search, currentMonth),
  );
  const monthOptions = useMemo(
    () =>
      Array.from({ length: MONTH_OPTION_COUNT }, (_, index) => shiftMonth(currentMonth, -index)),
    [currentMonth],
  );
  const window_ = useMemo(() => respiteWindow(state, today), [state, today]);

  useEffect(() => {
    const restore = () => setState(readRespiteState(window.location.search, currentMonth));
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [currentMonth]);

  const update = (patch: Partial<RespiteViewState>) => {
    const next = { ...state, ...patch };
    if (next.fromMonth > next.toMonth) {
      if (patch.fromMonth) next.toMonth = next.fromMonth;
      else next.fromMonth = next.toMonth;
    }
    setState(next);
    const search = writeRespiteState(window.location.search, next, currentMonth);
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${search}${window.location.hash}`,
    );
  };

  const query = useQuery(
    respiteSummaryQueryOptions({
      ...window_,
      clinic: state.clinic,
      navigator: state.navigator,
      provider: state.provider,
    }),
  );
  const options = query.data?.filterOptions;

  return (
    <DashboardsPageFrame className="flex flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Respite</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Home safety assessments against the 60-day goal, respite referrals by provider,
            respite-eligible carespaces by county, and open referrals that no longer fit GUIDE
            respite. The months select initial PAFs for HSAs and referral dates for referrals.
          </p>
        </div>
        <div className="text-xs font-semibold text-muted-foreground">
          Data: {query.data ? (query.data.source === "live" ? "LIVE" : "SAMPLE") : "—"} · Updated{" "}
          {formatTimestamp(query.data?.generatedAt)}
        </div>
      </header>

      <DashboardFilterRow>
        <FilterSelect
          id="respite-from"
          label="From"
          allLabel="—"
          value={state.fromMonth}
          options={monthOptions}
          onChange={(fromMonth) => fromMonth && update({ fromMonth })}
        />
        <FilterSelect
          id="respite-to"
          label="through"
          allLabel="—"
          value={state.toMonth}
          options={monthOptions}
          onChange={(toMonth) => toMonth && update({ toMonth })}
        />
        <FilterSelect
          id="respite-clinic"
          label="Clinic"
          allLabel="All clinics"
          value={state.clinic}
          options={options?.clinics ?? []}
          onChange={(clinic) => update({ clinic })}
        />
        <FilterSelect
          id="respite-navigator"
          label="Care navigator"
          allLabel="All CNs"
          value={state.navigator}
          options={options?.navigators ?? []}
          onChange={(navigator) => update({ navigator })}
        />
        <FilterSelect
          id="respite-provider"
          label="Provider"
          allLabel="All providers"
          value={state.provider}
          options={options?.providers ?? []}
          onChange={(provider) => update({ provider })}
        />
      </DashboardFilterRow>

      {query.data?.source === "mock" ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The live respite query is unavailable, so this page is showing sample numbers.
        </div>
      ) : null}

      {query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading Respite…
        </div>
      ) : null}
      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "Respite data is unavailable."}
        </div>
      ) : null}
      {query.data ? <RespiteContent summary={query.data} /> : null}

      <NeedsAttentionPanel filters={state} today={today} />
    </DashboardsPageFrame>
  );
}
